import { Skeleton } from '@material-ui/lab'
import React from 'react'

import VisuallyHidden from '../../components/VisuallyHidden'
import useStyles from './styles/NotificationListSkeleton.styles'

/**
 * Loading placeholder for the panels that render lists.
 *
 * `portal-ux-conventions.md` §7 puts skeleton rows at 71 screens for list
 * loading and reserves `<CircularProgress>` for buttons and inline waits. A
 * spinner in a list position collapses the page and then jumps to full height.
 *
 * **It takes its row spacing from the screen's own stylesheet**, not a copy.
 * §7 requires the skeleton be built "from the row component's own grid module",
 * and §2 explains why: shared constants are what stop the row and its skeleton
 * drifting apart. An earlier version of this file re-typed the numbers into a
 * parallel stylesheet — equivalent that day, and free to drift the next.
 *
 * `variant` exists because the two card styles genuinely differ: `inboxCard` is
 * `marginBottom 1.5 / padding 2`, `resourceCard` is `1 / 1.5`. One skeleton
 * cannot mirror both, and a version of this component claimed in a comment that
 * it did — leaving resource lists ~8px per row short, which is the layout jump
 * the component exists to prevent.
 */
export const NotificationListSkeleton = ({
  label,
  rows = 5,
  variant = 'inbox'
}: {
  /**
   * The accessible announcement, e.g. 'Loading deliveries'.
   *
   * Rendered as real TEXT inside the live region, not as an `aria-label`.
   * `role="status"` announces content changes within a region; an aria-label is
   * an accessible *name*, surfaced on focus or navigation — and this element is
   * neither focusable nor a landmark. A prior version put the label in the
   * attribute, which satisfied `getByLabelText` in a test while announcing
   * nothing to a screen reader.
   */
  label: string
  rows?: number
  variant?: 'inbox' | 'resource'
}) => {
  const classes = useStyles()
  const rowClass =
    variant === 'inbox' ? classes.inboxCard : classes.resourceCard

  return (
    <div className={classes.skeletonContainer}>
      <VisuallyHidden>
        <span role="status">{label}</span>
      </VisuallyHidden>
      {Array(rows)
        .fill('')
        .map((_, index) => (
          // aria-hidden: the rows are decorative. Without it they surface as
          // empty group content in the accessibility tree, competing with the
          // status text above that carries the actual meaning.
          <div aria-hidden="true" className={rowClass} key={index}>
            <Skeleton
              animation="wave"
              className={classes.skeletonLine}
              width="45%"
            />
            <Skeleton
              animation="wave"
              className={classes.skeletonLine}
              width="80%"
            />
          </div>
        ))}
    </div>
  )
}
